import React, { useState, useRef, useEffect } from 'react';
import * as XLSX from 'xlsx/xlsx.mjs';
import { IconButton, Tooltip } from '@mui/material';
import PublishIcon from '@mui/icons-material/Publish';
import PublishOutlinedIcon from '@mui/icons-material/PublishOutlined';
import PropTypes from 'prop-types';

const SheetJSFT = [
  'xlsx', 'xlsb', 'xlsm', 'xls', 'xml', 'csv', 'txt', 'ods', 'fods', 'uos',
  'sylk', 'dif', 'dbf', 'prn', 'qpw', '123', 'wb*', 'wq*', 'html', 'htm',
].map(x => '.' + x).join(',');

const ImportExcel = ({ name = "r4d", onImport, setCinchFlag, tabIdx }) => {
  const [file, setFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    if (file) {
      resetFileInput();
    }
  }, [tabIdx]);

  const resetFileInput = () => {
    setFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleChange = (e) => {
    const files = e.target.files;
    if (files && files[0]) {
      // Add file size validation (e.g., 10MB limit)
      if (files[0].size > 10 * 1024 * 1024) {
        setError('File size exceeds 10MB limit');
        resetFileInput();
        return;
      }
      setFile(files[0]);
      setError(null);
      handleFile(files[0]);
    }
  };

  const handleFile = (fileToProcess) => {
    setIsLoading(true);
    setError(null);
    const reader = new FileReader();
    const rABS = !!reader.readAsBinaryString;

    reader.onload = (e) => {
      try {
        const bstr = e.target.result;
        const wb = XLSX.read(bstr, {
          type: rABS ? 'binary' : 'array',
          bookVBA: true,
        });
        const wsname = wb.SheetNames[0];
        const ws = wb.Sheets[wsname];
        let newData;
        const ref = ws['!ref'];
        if (ref) {
          const range = XLSX.utils.decode_range(ref);
          range.e.c = Math.max(range.e.c, 100);
          const extendedRef = XLSX.utils.encode_range(range);
          newData = XLSX.utils.sheet_to_json(ws, { range: extendedRef, defval: '' });
        } else {
          newData = XLSX.utils.sheet_to_json(ws, { defval: '' });
        }
        const cleanRow = (row) => {
          const out = {};
          Object.keys(row || {}).forEach((k) => {
            if (k == null || k === '' || (typeof k === 'string' && (k.startsWith('_EMPTY') || k.startsWith('__EMPTY')))) return;
            out[k] = row[k];
          });
          return out;
        };
        const cleanedData = Array.isArray(newData) ? newData.map(cleanRow) : newData;
        setIsLoading(false);
        onImport(cleanedData);
      } catch (error) {
        setError('Error processing file: ' + error.message);
        setIsLoading(false);
      }
    };

    reader.onerror = () => {
      setError('Error reading file');
      setIsLoading(false);
    };

    if (rABS) {
      reader.readAsBinaryString(fileToProcess);
    } else {
      reader.readAsArrayBuffer(fileToProcess);
    }

    resetFileInput();
  };

  const handleSubmit = () => {
    if (name === 'cinch') {
      setCinchFlag?.();
    }
    fileInputRef.current?.click();
  };

  return (
    <>
      <input
        type="file"
        className="form-control"
        ref={fileInputRef}
        style={{ display: 'none' }}
        accept={SheetJSFT}
        onChange={handleChange}
      />
      <IconButton 
        aria-label="Import" 
        onClick={handleSubmit}
        disabled={isLoading}
      >
        {name === 'r4d' ? (
          <Tooltip title="r4d">
            <PublishIcon />
          </Tooltip>
        ) : (
          <Tooltip title="cinch">
            <PublishOutlinedIcon />
          </Tooltip>
        )}
      </IconButton>
      {error && (
        <div style={{ color: 'red', marginTop: '5px' }}>{error}</div>
      )}
    </>
  );
};

ImportExcel.propTypes = {
  name: PropTypes.string.isRequired,
  onImport: PropTypes.func.isRequired,
  setCinchFlag: PropTypes.func,
  tabIdx: PropTypes.number
};

export default ImportExcel;